import { RouteLocationRaw, RouteRecordRaw } from "vue-router";

const Podcasts = () => import(/* webpackChunkName: "core" */'@/content/pages/podcasts/index.vue');
const PodcastDetails = () => import(/* webpackChunkName: "core" */'@/content/pages/podcasts/details.vue');
const NewPodcast = () => import(/* webpackChunkName: "authoring" */'@/content/pages/podcasts/new.vue');
const Approver = () => import(/* webpackChunkName: "authoring" */'@/content/pages/approver/index.vue');
const PodcastStatistics = () => import(/* webpackChunkName: "stats" */'@/content/pages/podcasts/stats.vue');
const EpisodeStatistics = () => import(/* webpackChunkName: "stats" */'@/content/pages/podcasts/episodes/stats.vue');
const PodcastPublishing = () => import(/* webpackChunkName: "authoring" */'@/content/pages/podcasts/publishing.vue');
const Contributions = () => import(/* webpackChunkName: "profile" */'@/content/pages/profile/contributions/index.vue');
const ContributionsPodcast = () => import(/* webpackChunkName: "profile" */'@/content/pages/profile/contributions/podcasts/details.vue');
const EditPodcast = () => import(/* webpackChunkName: "authoring" */'@/content/pages/podcasts/edit.vue');
const Help = () => import(/* webpackChunkName: "help" */'@/content/pages/help/index.vue');
const NewEpisode = () => import(/* webpackChunkName: "authoring" */'@/content/pages/podcasts/episodes/new.vue');
const EditEpisode = () => import(/* webpackChunkName: "authoring" */'@/content/pages/podcasts/episodes/edit.vue');
const CookieNotice = () => import(/* webpackChunkName: "public" */'@/content/pages/public/cookie-notice.vue');
const TermsOfUse = () => import(/* webpackChunkName: "public" */'@/content/pages/public/terms-of-use.vue');
const NotFound = () => import(/* webpackChunkName: "errors" */'@/content/pages/error/not-found.vue');
const NotAllowed = () => import(/* webpackChunkName: "errors" */'@/content/pages/error/not-allowed.vue');
const Error = () => import(/* webpackChunkName: "errors" */'@/content/pages/error/index.vue');

const Routes = {
  Podcasts: {
    path: "/podcasts",
    component: Podcasts,
  },
  PodcastDetails: {
    path: '/:mode(public)?/podcasts/:podcastId/details',
    component: PodcastDetails,
    props: true,
  },
  PodcastDetailsWithEpisodes: {
    path: '/:mode(public)?/podcasts/:podcastId/episodes/:episodeId/details',
    component: PodcastDetails,
    props: true,
  },
  NewPodcast: {
    path: '/podcasts/new',
    component: NewPodcast,
    props: true,
  },
  EditPodcast: {
    path: '/podcasts/:podcastId/edit',
    component: EditPodcast,
    props: true,
  },
  Approver: {
    path: '/podcasts/approver',
    component: Approver,
    props: true,
  },
  PodcastPublishing: {
    path: '/podcasts/:podcastId/publishing',
    component: PodcastPublishing,
    props: true,
  },
  PodcastStatistics: {
    path: '/podcasts/:podcastId/stats',
    component: PodcastStatistics,
    props: true,
  },
  EpisodeStatistics: {
    path: '/podcasts/:podcastId/episodes/:episodeId/stats',
    component: EpisodeStatistics,
    props: true,
  },
  Contributions: {
    path: '/profile/contributions',
    component: Contributions,
    props: true,
  },
  ContributionsPodcast: {
    path: '/profile/contributions/podcasts/:podcastId/details',
    component: ContributionsPodcast,
    props: true,
  },
  Help: {
    path: '/help',
    component: Help,
    props: true,
  },
  NewEpisode: {
    path: '/podcasts/:podcastId/episodes/new',
    component: NewEpisode,
    props: true,
  },
  EditEpisode: {
    path: '/podcasts/:podcastId/episodes/:episodeId/edit',
    component: EditEpisode,
    props: true,
  },
  CookieNotice: {
    path: '/public/cookie-notice',
    component: CookieNotice,
    props: true
  },
  TermsOfUse: {
    path: '/public/terms-of-use',
    component: TermsOfUse,
    props: true
  },
  NotFound: {
    path: '/error/not-found',
    component: NotFound,
    props: true
  },
  NotAllowed: {
    path: '/error/not-allowed',
    component: NotAllowed,
    props: true
  },
  Error: {
    path: '/error',
    component: Error,
    props: true
  }
}

type RouteParams = {
  Podcasts: {}
  PodcastDetails: { podcastId: string, mode: "public" | undefined }
  PodcastDetailsWithEpisodes: { podcastId: string, episodeId: string, mode: "public" | undefined }
  NewPodcast: {},
  EditPodcast: { podcastId: string },
  Approver: {}
  PodcastPublishing: { podcastId: string },
  PodcastStatistics: { podcastId: string },
  EpisodeStatistics: { podcastId: string, episodeId: string },
  Contributions: {},
  ContributionsPodcast: { podcastId: string }
  Help: {},
  NewEpisode: { podcastId: string }
  EditEpisode: { podcastId: string, episodeId: string }
  CookieNotice: {},
  TermsOfUse: {},
  NotFound: {},
  NotAllowed: {},
  Error: {}
}
type RouteName = keyof typeof Routes

export const RouteNames = Object.fromEntries(Object.keys(Routes).map(it => [it as RouteName, it])) as { [key in RouteName]: string }


export const ROUTE = Object.fromEntries(
  Object.entries(Routes).map(([name]) =>
    [name as keyof RouteName, (params: any, query?: {}) => {
      const parsedQuery = query != null
        ? Object.fromEntries(
          Object.entries(query).map(([key, value]) => {
            if (typeof value === "object") {
              return [key, JSON.stringify(value)]
            } else {
              return [key, value]
            }
          })
        )
        : undefined;
      return { name: name, params, query: parsedQuery }
    }]
  )
) as { [key in RouteName]: (params: RouteParams[key], query?: {}) => RouteLocationRaw }

const routes: Array<RouteRecordRaw> = [
  ...Object.entries(Routes).map(([name, route]) => ({ name, ...route })),
  {
    path: '/',
    redirect: ROUTE.Podcasts({})
  },
  {
    path: '/:catchAll(.*)',
    redirect: ROUTE.NotFound({})
  }
]

export default routes
